import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
    Avatar,
} from "@heroui/react";
import {
    LuEye,
    LuPencil,
    LuTrash,
    LuPlus,
    LuRefreshCw,
    LuCircleUserRound,
} from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
    FilterButton,
    ChangeStatusModal,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
    NoImageFallback,
    ImageLightbox,
} from "@/components/ui";
import { FilterData } from "./components";
import { formatPhoneInternational } from "@/utils";
import { useUser } from "@/context/UserContext";

// Initial filter values
const initialFilters = {
    name: "",
    alamat: "",
    kontak: "",
    status_id: "",
};

const CustomerData = () => {
    const { hasPermission } = useUser();
    const [lightbox, setLightbox] = useState({ isOpen: false, images: [] });
    const [statusActiveModal, setStatusActiveModal] = useState({
        isOpen: false,
        item: null,
    });

    const table = useDataTable(
        "customers/data",
        "customers/{id}/delete",
        {},
        initialFilters,
    );

    // Fetch filter options for label lookups
    const { data: activeStatuses } = useFetchData("select/active-status");

    // Filter options to pass to FilterData component (handle null with || [])
    const filterOptions = { statuses: activeStatuses || [] };

    // Filter labels with dynamic getValueLabel
    const filterLabels = {
        name: { label: "Nama" },
        alamat: { label: "Alamat" },
        kontak: { label: "Kontak" },
        status_id: {
            label: "Status",
            getValueLabel: (val) => {
                const item = activeStatuses.find(
                    (s) => String(s.value) === String(val),
                );
                return item?.title || val;
            },
        },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data Pelanggan"
                    description="Terjadi kesalahan saat memuat data pelanggan. Silakan coba lagi."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Pelanggan", skeletonWidth: "w-56" },
        { name: "Alamat", skeletonWidth: "w-48" },
        { name: "Kontak", skeletonWidth: "w-32" },
        { name: "Status Keaktifan", skeletonWidth: "w-24" },
        { name: "Aksi", skeletonWidth: "w-20", align: "end" },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Pelanggan"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Pelanggan" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("customer.create") && (
                        <Button
                            as={Link}
                            to="/customers/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Pelanggan
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={filterOptions}
            />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Data Kosong"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada pelanggan yang cocok dengan filter."
                                    : "Belum ada data pelanggan."
                            }
                        />
                    ) : (
                        <Table aria-label="Tabel Pelanggan" shadow="none">
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="customer">
                                    Pelanggan
                                </TableColumn>
                                <TableColumn key="address">Alamat</TableColumn>
                                <TableColumn key="contact">Kontak</TableColumn>
                                <TableColumn key="status">
                                    Status Keaktifan
                                </TableColumn>
                                <TableColumn align="end" key="actions">
                                    Aksi
                                </TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        {/* No. */}
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {(table.pagination.page - 1) *
                                                    table.pagination.perPage +
                                                    table.items.indexOf(item) +
                                                    1}
                                                .
                                            </ThemedText>
                                        </TableCell>

                                        {/* Pelanggan (Nama + Deskripsi + Avatar) */}
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <Avatar
                                                    size="sm"
                                                    radius="full"
                                                    showFallback
                                                    src={
                                                        item.profile_image
                                                            ?.url_600
                                                    }
                                                    name={item.name}
                                                    className={
                                                        item.images?.length > 0
                                                            ? "shrink-0 cursor-pointer"
                                                            : "shrink-0"
                                                    }
                                                    onClick={() =>
                                                        item.images?.length >
                                                            0 &&
                                                        setLightbox({
                                                            isOpen: true,
                                                            images: item.images,
                                                        })
                                                    }
                                                    fallback={
                                                        <NoImageFallback
                                                            icon={
                                                                LuCircleUserRound
                                                            }
                                                            iconSize={18}
                                                        />
                                                    }
                                                />
                                                <div className="flex flex-col">
                                                    <ThemedText size="sm">
                                                        {item.name}
                                                    </ThemedText>
                                                    {item.description && (
                                                        <ThemedText
                                                            size="xs"
                                                            variant="secondary"
                                                            className="line-clamp-1"
                                                        >
                                                            {item.description}
                                                        </ThemedText>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>

                                        {/* Alamat */}
                                        <TableCell>
                                            <ThemedText
                                                size="xs"
                                                className="line-clamp-2"
                                            >
                                                {item.first_address
                                                    ?.full_address || "-"}
                                            </ThemedText>
                                        </TableCell>

                                        {/* Kontak */}
                                        <TableCell>
                                            {item.first_contact_number ? (
                                                <div className="flex flex-col">
                                                    <ThemedText
                                                        size="sm"
                                                        className="whitespace-nowrap"
                                                    >
                                                        {formatPhoneInternational(
                                                            item
                                                                .first_contact_number
                                                                .number,
                                                        )}
                                                    </ThemedText>
                                                    <ThemedText
                                                        size="xs"
                                                        variant="secondary"
                                                    >
                                                        {item
                                                            .first_contact_number
                                                            .contact_type
                                                            ?.name || ""}
                                                    </ThemedText>
                                                </div>
                                            ) : (
                                                <ThemedText
                                                    size="sm"
                                                    variant="secondary"
                                                >
                                                    -
                                                </ThemedText>
                                            )}
                                        </TableCell>

                                        {/* Status Keaktifan */}
                                        <TableCell>
                                            <Chip
                                                color={
                                                    item.active_status
                                                        ?.badge_color
                                                }
                                                size="sm"
                                                className="font-medium"
                                                variant="flat"
                                            >
                                                {item.active_status?.name ||
                                                    "Unknown"}
                                            </Chip>
                                        </TableCell>

                                        {/* Aksi */}
                                        <TableCell>
                                            <div className="relative flex items-center justify-end gap-2">
                                                <Tooltip content="Detail Pelanggan">
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="light"
                                                        as={Link}
                                                        to={`/customers/${item.id}/detail`}
                                                    >
                                                        <LuEye
                                                            size={14}
                                                            className="text-default-500"
                                                        />
                                                    </Button>
                                                </Tooltip>
                                                {hasPermission("customer.edit") && (
                                                    <Tooltip content="Edit Pelanggan">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            as={Link}
                                                            to={`/customers/${item.id}/edit`}
                                                        >
                                                            <LuPencil
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {hasPermission("customer.edit") && (
                                                    <Tooltip content="Ubah Status Keaktifan">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            onPress={() =>
                                                                setStatusActiveModal(
                                                                    {
                                                                        isOpen: true,
                                                                        item: item,
                                                                    },
                                                                )
                                                            }
                                                        >
                                                            <LuRefreshCw
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {hasPermission("customer.delete") && (
                                                    <Tooltip content="Hapus Pelanggan">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            color="danger"
                                                            variant="light"
                                                            onPress={() =>
                                                                table.deleteState.openDeleteModal(
                                                                    item.id,
                                                                )
                                                            }
                                                        >
                                                            <LuTrash size={14} />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal table={table} />

            {/* Image Lightbox */}
            <ImageLightbox
                isOpen={lightbox.isOpen}
                onClose={() => setLightbox({ isOpen: false, images: [] })}
                images={lightbox.images}
                urlKey="url_1000"
            />

            {/* Status Change Modal */}
            <ChangeStatusModal
                isOpen={statusActiveModal.isOpen}
                onClose={() =>
                    setStatusActiveModal({ isOpen: false, item: null })
                }
                item={statusActiveModal.item}
                itemName={statusActiveModal.item?.name}
                statusEndpoint="select/active-status"
                statusField="active_status_id"
                endpoint="customers/{id}/change-active-status"
                onSuccess={() => table.refetch()}
            />
        </div>
    );
};

export default CustomerData;
